import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {RadarFornecedorApiService} from '../../services/RadarFornecedorApi.service'
import {BuscaFornecedorOut, RadarFornecedorSocketMessage} from '../../types/radarFornecedor'

const TETO_MS = 5 * 60 * 1000
const ESPERA_COM_PUSH_MS = 45000
const ESPERA_SEM_PUSH_MS = 15000
const AVISO_DEMORA = 'A busca continua em processamento. Verifique o andamento novamente ou tente o CNPJ.'

interface BuscaSalva {
    id: string
    termo: string
    iniciadaEm: number
}

interface Acompanhamento extends Omit<BuscaSalva, 'id'> {
    id: string | null
    lendo?: boolean
    reler?: boolean
    pausada?: boolean
}

interface EstadoBuscaFornecedor {
    busca: BuscaFornecedorOut | null
    termo: string
    buscando: boolean
    erro: string | null
    recuperada: BuscaSalva | null
    podeVerificar: boolean
    iniciar: (termo: string) => Promise<void>
    limpar: () => void
    verificar: () => void
}

// O origin é parte do escopo do localStorage. A URL da API separa ambientes que
// compartilham o mesmo origin (por exemplo, ao alternar a configuração local).
export function chaveBuscaFornecedor(userId: string): string {
    return `radar-fornecedor:busca:${encodeURIComponent(import.meta.env.VITE_PRICE_RESEARCH_URL || '')}:${userId}`
}

export function useBuscaFornecedor({api, userId, mensagem, wsAberto, wsReconexoes}: {
    api: RadarFornecedorApiService
    userId: string
    mensagem: RadarFornecedorSocketMessage
    wsAberto: boolean
    wsReconexoes: number
}): EstadoBuscaFornecedor {
    const [busca, setBusca] = useState<BuscaFornecedorOut | null>(null)
    const [iniciando, setIniciando] = useState(false)
    const [observando, setObservando] = useState(false)
    const [erro, setErro] = useState<string | null>(null)
    const [recuperada, setRecuperada] = useState<BuscaSalva | null>(null)
    const ativo = useRef<Acompanhamento | null>(null)
    const chave = useMemo(() => userId ? chaveBuscaFornecedor(userId) : null, [userId])

    const salvar = useCallback((valor: Acompanhamento | null) => {
        if (!chave) return
        try {
            if (valor?.id) {
                localStorage.setItem(chave, JSON.stringify({
                    id: valor.id, termo: valor.termo, iniciadaEm: valor.iniciadaEm,
                }))
            } else localStorage.removeItem(chave)
        } catch { /* armazenamento indisponível não impede a busca */ }
    }, [chave])

    const aplicar = useCallback((resultado: BuscaFornecedorOut, atual: Acompanhamento) => {
        if (ativo.current !== atual || resultado.id !== atual.id) return
        setBusca(resultado)
        setErro(atual.pausada ? AVISO_DEMORA : null)
        if (resultado.status === 'CONCLUIDO' || resultado.status === 'ERRO') {
            setErro(null)
            setObservando(false)
            ativo.current = null
            salvar(null)
            if (resultado.status === 'ERRO') {
                setErro(resultado.error_message || 'Não foi possível consultar o PNCP agora. Tente novamente ou use o CNPJ.')
            }
        }
    }, [salvar])

    const ler = useCallback(async (atual: Acompanhamento) => {
        if (!atual.id || ativo.current !== atual) return
        // Um evento pode chegar durante o GET inicial. Releia ao terminar para
        // não perder a conclusão que aconteceu depois de o GET começar.
        if (atual.lendo) {
            atual.reler = true
            return
        }
        atual.lendo = true
        try {
            do {
                atual.reler = false
                try {
                    aplicar(await api.getBusca(atual.id), atual)
                } catch (e: unknown) {
                    if (ativo.current !== atual) return
                    const status = (e as {response?: {status?: number}})?.response?.status
                    if (status === 404 || status === 403) {
                        ativo.current = null
                        salvar(null)
                        setObservando(false)
                        setErro('Não foi possível recuperar esta busca. Inicie uma nova busca.')
                    } else {
                        setErro(atual.pausada ? AVISO_DEMORA : 'Não foi possível atualizar o andamento. Vamos tentar novamente.')
                    }
                }
            } while (atual.reler && ativo.current === atual)
        } finally {
            atual.lendo = false
        }
    }, [api, aplicar, salvar])

    const limpar = useCallback(() => {
        ativo.current = null // para somente o acompanhamento; o job continua no servidor
        salvar(null)
        setBusca(null)
        setIniciando(false)
        setObservando(false)
        setErro(null)
        setRecuperada(null)
    }, [salvar])

    const iniciar = useCallback(async (termo: string) => {
        const nome = termo.trim()
        if (ativo.current?.termo.toLocaleLowerCase() === nome.toLocaleLowerCase()) return
        const atual: Acompanhamento = {id: null, termo: nome, iniciadaEm: Date.now()}
        ativo.current = atual
        salvar(null)
        setBusca(null)
        setIniciando(true)
        setObservando(false)
        setErro(null)
        try {
            const resultado = await api.criarBusca(nome)
            if (ativo.current !== atual) return
            setIniciando(false)
            atual.id = resultado.id
            salvar(atual)
            setObservando(true)
            aplicar(resultado, atual)
            // O job pode ter terminado antes da assinatura do watcher/push.
            await ler(atual)
        } catch {
            if (ativo.current !== atual) return
            ativo.current = null
            setIniciando(false)
            setObservando(false)
            setErro('Não foi possível iniciar a busca por nome. Tente novamente ou use o CNPJ.')
        }
    }, [api, aplicar, ler, salvar])

    const verificar = useCallback(() => {
        const atual = ativo.current
        if (!atual?.id) return
        atual.iniciadaEm = Date.now()
        atual.pausada = false
        salvar(atual)
        setObservando(true)
        setErro(null)
        void ler(atual)
    }, [ler, salvar])

    useEffect(() => {
        ativo.current = null
        setBusca(null)
        setIniciando(false)
        setObservando(false)
        setErro(null)
        setRecuperada(null)
        if (chave) {
            try {
                const valor: BuscaSalva = JSON.parse(localStorage.getItem(chave) || 'null')
                if (valor && typeof valor.id === 'string' && typeof valor.termo === 'string' &&
                    typeof valor.iniciadaEm === 'number' && Number.isFinite(valor.iniciadaEm)) {
                    const atual = {...valor, iniciadaEm: Math.min(valor.iniciadaEm, Date.now())}
                    ativo.current = atual
                    setRecuperada(valor)
                    setBusca({...valor, status: 'PENDENTE', candidatos: [], error_message: null})
                    setObservando(true)
                    void ler(atual)
                }
            } catch { /* metadados antigos ou inválidos: começa normalmente */ }
        }
        return () => { ativo.current = null }
    }, [chave, ler])

    useEffect(() => {
        const atual = ativo.current
        if (atual?.id && mensagem.busca_id === atual.id &&
            (mensagem.status === 'CONCLUIDO' || mensagem.status === 'ERRO')) void ler(atual)
    }, [mensagem, ler])

    useEffect(() => {
        const reconciliar = () => {
            if (document.visibilityState === 'hidden') return
            if (ativo.current?.id) void ler(ativo.current)
        }
        window.addEventListener('focus', reconciliar)
        window.addEventListener('online', reconciliar)
        document.addEventListener('visibilitychange', reconciliar)
        if (wsAberto) reconciliar()
        return () => {
            window.removeEventListener('focus', reconciliar)
            window.removeEventListener('online', reconciliar)
            document.removeEventListener('visibilitychange', reconciliar)
        }
    }, [wsAberto, wsReconexoes, ler])

    useEffect(() => {
        const atual = ativo.current
        if (!observando || !atual?.id) return
        const restante = Math.max(0, atual.iniciadaEm + TETO_MS - Date.now())
        const limite = setTimeout(() => {
            if (ativo.current !== atual) return
            atual.pausada = true
            setObservando(false)
            setErro(AVISO_DEMORA)
        }, restante)
        const intervalo = setInterval(() => {
            if (document.visibilityState !== 'hidden') void ler(atual)
        }, wsAberto ? ESPERA_COM_PUSH_MS : ESPERA_SEM_PUSH_MS)
        return () => {
            clearTimeout(limite)
            clearInterval(intervalo)
        }
    }, [observando, busca?.id, wsAberto, ler])

    return {busca, termo: busca?.termo || ativo.current?.termo || '',
        buscando: iniciando || observando, erro, recuperada, iniciar, limpar, verificar,
        podeVerificar: !!ativo.current?.id && !!busca && busca.status !== 'CONCLUIDO' && busca.status !== 'ERRO'}
}
